/**
 * Chapter 3 Optimized Layout Component
 * 
 * Compact, no-scroll reading experience specifically designed for Chapter 3.
 * Everything fits in one viewport: image, text, and navigation controls.
 * Uses efficient space utilization and compact navigation bar.
 * Follows Chapter 2's proven layout system with dynamic height adjustments.
 */

import { motion } from "motion/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CueInteraction } from "@/components/cue-interaction";
import { Chapter3CueChoice } from "@/components/chapter-3-cue-choice";
import { ForestJourneyMap } from "@/components/forest-journey-map";
import { BridgeScene } from "@/components/bridge-scene";
import { MaxWorriedBridgeScene } from "@/components/max-worried-bridge-scene";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { saveBookmark } from "@/lib/bookmark";
import { updateCurrentPosition } from "@/lib/platform-tracking";
import { ArrowLeft, ArrowRight, Home, BookOpen, Shield } from "lucide-react";

// Chapter 3 spreads data with Cloudflare Stream videos (11 videos for story spreads, no video on cue page)
const chapter3Data = {
  title: "Chapter 3: The Brave Bridge",
  subtitle: "Bravery",
  cue: "Bravery Badge",
  cueIcon: Shield,
  cueColor: "text-blue-600",
  bgColor: "bg-blue-50",
  spreads: [
    {
      spread: 0,
      section: "map",
    },
    {
      spread: 1,
      section: "story",
      imageUrl: "https://static.step1.dev/9fd3a706a5d1f1702b206e9848dba48e",
      videoId: "5609e7a1fbd024b54af3acc3806e051f",
      text: "Mila and her friends were buzzing with excitement as they followed the next twist on their mystery map. The path led them to the edge of a wide, wobbly wooden bridge stretching over a sparkling stream."
    },
    {
      spread: 2,
      section: "story",
      imageUrl: "https://static.step1.dev/c14f4e51d99ab263dfe03c8ad82c6145",
      videoId: "64cf8c88da6c6ce461e50bff623e2fa5",
      text: "On the other side, a colorful banner fluttered in the breeze: \"Forest Festival—This Way!\"\n\nMila's paws tingled. \"Look! The festival is just across the bridge!\" she squeaked."
    },
    {
      spread: 3,
      section: "story",
      imageUrl: "https://static.step1.dev/5de50364bb25d046926df4c2c0225a64",
      videoId: "23f3446ace6aa513b76b303c22926c20",
      text: "But Pip the porcupine stepped back, his whiskers trembling. \"That bridge looks really high...and really wobbly,\" he whispered. \"What if it creaks? What if I slip? What if I turn into a pancake and they have to roll me to the festival?\""
    },
    {
      spread: 4,
      section: "story",
      imageUrl: "https://static.step1.dev/3286ff07dade2b26703dc592171ad8af",
      videoId: "6bfb07b7a408fceb8db3a8ee95d0dd53",
      text: "Bella the bunny hopped to his side. \"It's okay to feel scared, Pip. Want to try crossing together?\"\n\nPip nodded, but his tummy felt twisty."
    },
    {
      spread: 5,
      section: "story",
      imageUrl: "https://static.step1.dev/99a12aca3cd25829d82cf68900df6086",
      videoId: "fc478d4860af5f7629d58521cbc3fe7e",
      text: "Bella smiled gently. \"Let's take one small step at a time. And if you feel scared, tell yourself, 'I can try!' and then take a deep, slow breath—like this.\" She puffed up her cheeks and breathed in... and out... nice and slow."
    },
    {
      spread: 6,
      section: "cue",
      visualComponent: "BridgeScene",
      // NO VIDEO for cue page - Interactive Bravery Badge activity
      cue: "Bravery Badge",
      cueText: "Brave Bridge Step",
      exerciseSteps: [
        "Let's try this together:",
        "**Chant:** \"I can try!\"",
        "**Do:** Take one tiny step forward (gentle foot tap).",
        "**Say:** \"I'll try [small, specific action].\""
      ],
      buttonLabel: "I tried!"
    },
    {
      spread: 7,
      section: "story",
      imageUrl: "https://static.step1.dev/7efcd1e4ca26fd5b9461b457157f0c09",
      videoId: "a2c690f66f6705e16e864a69d4df131f",
      text: "Pip put one paw on the bridge. Creak! He squeezed Bella's paw tighter and took a deep breath."
    },
    {
      spread: 8,
      section: "story",
      imageUrl: "https://static.step1.dev/744b8a549d2bcb18ee02ab6fe70c0118",
      videoId: "fbfd18c45ba017843bbc287159e09c5b",
      text: "Pip's heart thumped, but Step by step, the friends moved forward. Bella cheered, \"You're doing it, Pip!\""
    },
    {
      spread: 9,
      section: "story",
      imageUrl: "https://static.step1.dev/6b0ed1c7ed9dd557c893422c3c284488",
      videoId: "36e1b6fd60527831fe459b77dbb496bc",
      text: "Halfway across, the wind whooshed. Pip's ears trembled. He wanted to turn back, but he remembered Bella's words: \"I can try!\"\n\nPip whispered to himself, \"I can try. I can try.\" He took another deep breath and kept going, feeling braver with each step."
    },
    {
      spread: 10,
      section: "story",
      imageUrl: "https://static.step1.dev/5d6ac3aab41af4695dc9bcdc11f23cf7",
      videoId: "3e413a4b1d58250c1b26f330fc8535e6",
      text: "Pip smiled. \"I was scared, but I tried anyway. Next time, maybe I'll ask for help instead of screaming—or at least warn everyone before I turn into a pancake!\""
    },
    {
      spread: 11,
      section: "story",
      imageUrl: "https://static.step1.dev/b084365215453d317fde9d714b003f7b",
      videoId: "c8e78a815f42ec98915054636904d818",
      text: "At last, all four friends reached the other side. The festival music played, and the smell of treats filled the air.\n\nPip's tummy still felt fluttery, but now it was with excitement.\n\nMila grinned. \"You were so brave, Pip!\""
    },
    {
      spread: 12,
      section: "story",
      imageUrl: "https://static.step1.dev/0fa7ab2a04cd69b5284f65d9745e0d43",
      videoId: "c53c0eb4ed96c903b50594aec680a1be",
      text: "Grandpa Tortoise nodded. \"Exactly. Every brave step helps us grow.\"\n\nThe friends danced and laughed at the festival, proud of their brave steps—and ready for whatever the next adventure might bring."
    }
  ]
};

// Visual component mapping
const visualComponents = {
  BridgeScene,
  MaxWorriedBridgeScene
};

interface Chapter3OptimizedProps {
  onBackToContents: () => void;
}export function Chapter3Optimized({ onBackToContents }: Chapter3OptimizedProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Initialize spread from URL parameter or default to 0
  const spreadParam = searchParams.get('spread');
  const initialSpread = spreadParam ? parseInt(spreadParam) : 0;
  const [currentSpread, setCurrentSpread] = useState(initialSpread);
  const [completedCues, setCompletedCues] = useState<Set<string>>(new Set());

  const handleCueComplete = () => {
    setCompletedCues(prev => new Set(prev).add('bravery-badge-cue'));
  };

  const totalSpreads = chapter3Data.spreads.length;
  const currentSpreadData = chapter3Data.spreads[currentSpread];
  
  // Reset spread when coming from bookmark
  useEffect(() => {
    const spreadParam = searchParams.get('spread');
    const targetSpread = spreadParam ? parseInt(spreadParam) : 0;
    setCurrentSpread(targetSpread);
  }, [searchParams]);
  
  // Save bookmark when spread changes
  useEffect(() => {
    saveBookmark(3, currentSpread, chapter3Data.title);
  }, [currentSpread]);

  // Update platform tracking when position changes
  useEffect(() => {
    updateCurrentPosition(3, currentSpread);
  }, [currentSpread]);

  // 🎯 Send analytics to PostgreSQL using new API
  useEffect(() => {
    const sendAnalyticsAsync = async () => {
      try {
        const { sendAnalytics } = await import('@/lib/analytics/client');
        await sendAnalytics(3, currentSpread + 1);
        console.log(`✅ Enhanced analytics sent to PostgreSQL - Chapter 3, Spread ${currentSpread + 1}`);
      } catch (error) {
        console.warn('Failed to send enhanced analytics:', error);
      }
    };
    sendAnalyticsAsync();
  }, [currentSpread]);

  const handleNextSpread = useCallback(() => {
    if (currentSpread < totalSpreads - 1) {
      setCurrentSpread(currentSpread + 1);
    } else {
      // Go to Activities page after the last story spread
      navigate("/book/chapter/3/activity");
    }
  }, [currentSpread, totalSpreads, navigate]);

  const handlePrevSpread = useCallback(() => {
    if (currentSpread > 0) {
      setCurrentSpread(currentSpread - 1);
    } else {
      navigate("/book/contents");
    }
  }, [currentSpread, navigate]);

  const renderVisual = (componentName?: string, imageUrl?: string, videoId?: string) => {
    // PRIORITY: Cloudflare Stream video if available
    if (videoId) {
      return (
        <div className="w-full h-full rounded-lg overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50 max-w-5xl mx-auto">
          <iframe
            key={videoId}
            src={`https://iframe.videodelivery.net/${videoId}?autoplay=true&muted=true&controls=false&preload=auto&fit=crop`}
            style={{ 
              border: 'none', 
              width: '120%',
              height: '120%',
              position: 'relative',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none'
            }}
            allow="autoplay; encrypted-media"
            title={`Chapter 3 Spread ${currentSpread + 1} Video`}
            onLoad={() => {
              console.log(`✅ Cloudflare Stream iframe loaded - Chapter 3, Spread ${currentSpread + 1}, ID: ${videoId}`);
            }}
          />
        </div>
      );
    }

    // Fallback to static image if no video
    if (imageUrl) {
      return (
        <div className="h-full flex items-center justify-center overflow-hidden">
          <motion.img
            src={imageUrl}
            alt={`Chapter 3 Spread ${currentSpread + 1}`}
            className="w-full h-full max-w-4xl object-contain mx-auto"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
      );
    }

    // Visual component for cue pages
    if (componentName) {
      const Component = visualComponents[componentName as keyof typeof visualComponents];
      if (Component) {
        return (
          <div className="h-full flex items-center justify-center overflow-hidden">
            <Component className="max-h-full max-w-full object-contain" />
          </div>
        );
      }
    }
    return null;
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50">
      
      {/* COMPACT HEADER - TRANSLUCENT */}
      <div className="bg-white/30 backdrop-blur-sm border-b border-blue-200/50 px-4 py-2 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          
          {/* Left: Back + Chapter Title */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-forest-600 to-golden-600 rounded-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-forest-800">Dira Books</div>
              </div>
            </div>
            <div className="text-forest-400">•</div>
            <Button
              onClick={onBackToContents}
              variant="ghost"
              size="sm"
              className="text-forest-700 px-2 py-1 h-8 hover:bg-transparent hover:text-forest-600"
            >
              <BookOpen className="w-4 h-4 mr-1" />
              Contents
            </Button>
            <div className="text-forest-400">•</div>
            <h1 className="text-lg font-bold text-forest-700">
              Chapter 3: The Brave Bridge <span className="text-sm font-normal text-forest-600">• Bravery</span>
            </h1>
          </div>
          
          {/* Right: Badge + Activities */}
          <div className="flex items-center gap-2">
            <Badge 
              className="text-xs px-2 py-1 border bg-white/50 backdrop-blur-sm" 
              style={{ 
                color: '#5D4E37', 
                borderColor: '#E6D2AA' 
              }}
            >
              🛡️ Bravery Badge
            </Badge>
            
            <Button
              onClick={async () => {
                // 🎯 Enhanced activities button tracking using new API
                try {
                  const { trackButton, sendAnalytics } = await import('@/lib/analytics/client');
                  
                  trackButton('activities_button', {
                    navigation_source: 'chapter_nav'
                  });
                  
                  await sendAnalytics(3, currentSpread + 1);
                  
                  console.log(`📊 Enhanced Activities button tracking:`, {
                    chapter: 3,
                    spread: currentSpread + 1,
                    buttonType: 'activities_button'
                  });
                } catch (error) {
                  console.warn('Failed to track activities button:', error);
                }
                
                navigate("/book/chapter/3/activity");
              }}
              variant="outline"
              size="sm"
              className="text-xs px-2 py-1 h-8 border-blue-300 text-blue-700 bg-white/20 hover:bg-transparent hover:text-forest-600"
            >
              🎯 Activities
            </Button>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col overflow-hidden">
        
        {currentSpreadData?.section === "map" ? (
          /* FULL-SCREEN MAP PAGE (Spread 0) */
          <div className="fixed inset-0 z-50">
            <ForestJourneyMap currentChapter={3} />
          </div>
        ) : currentSpreadData?.section === "cue" ? (
          /* CUE PAGE - FULL PAGE LAYOUT WITH BACKGROUND IMAGE */
          <div className="flex-1 relative overflow-hidden">
            
            {/* Background Image */}
            <div className="absolute inset-0">
              {renderVisual(currentSpreadData?.visualComponent, currentSpreadData?.imageUrl, currentSpreadData?.videoId)}
              <div className="absolute inset-0 bg-gradient-to-b from-blue-400/40 via-blue-300/60 to-blue-500/70"></div>
            </div>

            {/* Main Cue Box - Centered */}
            <div className="relative z-10 h-full flex items-center justify-center p-8">
              <motion.div 
                className="bg-cream-50 border-4 border-blue-400 rounded-3xl p-12 text-center mx-auto max-w-2xl"
                style={{ 
                  backgroundColor: '#FDF6E3',
                  borderColor: '#3B82F6'
                }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                {/* Celebratory Emojis at Top */}
                <div className="text-5xl mb-8">
                  🛡️ ✨ 🛡️
                </div>
                
                {/* Magic Cue Found Title */}
                <h2 className="text-4xl font-bold mb-10" style={{ color: '#2563EB' }}>
                  ✨ Magic Cue Found! ✨
                </h2>
                
                {/* Instruction Text */}
                <div className="text-3xl leading-relaxed font-medium mb-6" style={{ color: '#3B82F6' }}>
                  Click the glowing Bravery Badge in the corner to try the activity!
                </div>
              </motion.div>
            </div>

            {/* Cue Interaction */}
            <div className="absolute top-8 right-8 z-20">
              <Chapter3CueChoice
                onComplete={handleCueComplete}
                isCompleted={completedCues.has('bravery-badge-cue')}
              />
            </div>
          </div>
        ) : (
          /* STORY PAGES - SPLIT LAYOUT */
          <>
            {/* TOP: VIDEO SECTION - 70% of screen height */}
            <div className="h-[70%] bg-transparent overflow-hidden relative">
              <div className="h-full p-2 flex items-center justify-center">
                {renderVisual(currentSpreadData?.visualComponent, currentSpreadData?.imageUrl, currentSpreadData?.videoId)}
              </div>
            </div>
            
            {/* BOTTOM: TEXT SECTION - 30% height, left-aligned */}
            <div className="h-[30%] flex flex-col px-8 py-6">
              <div className="flex-1 flex items-center">
                <div className="text-xl leading-relaxed text-gray-900 max-w-5xl mx-auto" style={{ textAlign: 'left' }}>
                  {currentSpreadData?.text?.split('\n\n').map((paragraph, index) => (
                    <p 
                      key={index} 
                      className={index > 0 ? 'mt-4' : ''}
                      style={{ textAlign: 'left' }}
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Minimal Navigation Controls */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex justify-between items-center py-0 px-2">
          {/* Previous Button - Ultra Minimal */}
          <Button
            onClick={handlePrevSpread}
            disabled={currentSpread === 0}
            variant="ghost"
            size="sm"
            className="text-forest-600 disabled:opacity-50 px-2 py-0 h-6 hover:bg-transparent hover:text-forest-600"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Prev
          </Button>

          {/* Chapter Progress with Dots */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-forest-600">Chapter Progress</span>
            <div className="flex gap-1">
              {chapter3Data.spreads.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSpread(index)}
                  className={`w-2 h-2 rounded-full  ${
                    index === currentSpread
                      ? 'bg-forest-500'
                      : 'bg-forest-200'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-forest-600">{currentSpread} of {chapter3Data.spreads.length - 1}</span>
          </div>

          {/* Next Button - Ultra Minimal */}
          <Button
            onClick={handleNextSpread}
            variant="ghost"
            size="sm"
            className="text-forest-600 px-2 py-0 h-6 hover:bg-transparent hover:text-forest-600"
          >
            {currentSpread === totalSpreads - 1 ? "Activities" : "Next"}
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
